import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { api, setSessionRefresher } from '../api/client'
import type { ActivityModule, SignInResponse } from '../api/client'

const STORAGE_KEY = 'agri.session'

/** La session du portail d'administration : une autre clé, pour que les deux portails ne se marchent pas dessus. */
export const ADMIN_STORAGE_KEY = 'sanabil.admin.session'

interface Session {
  token: string
  expiresAt: string
  refreshToken: string
  refreshExpiresAt: string
  fullName: string
  email: string
  role: string
  organizationId: number
}

interface AuthValue {
  session: Session | null
  modules: ActivityModule[]
  /** `remember` faux : la session vit dans l'onglet et meurt avec lui (« Rester connecté » décoché). */
  signIn: (email: string, password: string, remember?: boolean) => Promise<void>
  /** Adopte une session obtenue autrement que par `signIn` : la double authentification du portail. */
  adopt: (response: SignInResponse) => void
  signOut: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * La session est conservée dans le stockage local : c'est le compromis habituel d'une
 * application à page unique, au prix d'une exposition au vol de jeton par script injecté.
 * Un cookie « HttpOnly » serait plus sûr et suppose que l'API et le site partagent un
 * domaine — décision consignée dans docs/adr/0007-session-navigateur.md.
 *
 * Le jeton d'accès est COURT ; la session vit par le jeton de rafraîchissement, que le
 * serveur fait tourner à chaque usage et révoque à la déconnexion. Un 401 en cours de
 * route déclenche une rotation, puis le rejeu de l'appel ; si la rotation échoue, la
 * session est purgée et l'écran revient à la connexion. Avant, la déconnexion ne
 * révoquait rien et un jeton de huit heures restait valable après « Se déconnecter ».
 */
/** Où vit la session : le stockage local survit au navigateur, celui de session à l'onglet. */
function storageOf(remember: boolean): Storage {
  return remember ? window.localStorage : window.sessionStorage
}

function loadSession(storageKey: string): Session | null {
  return readSession(window.localStorage, storageKey) ?? readSession(window.sessionStorage, storageKey)
}

function readSession(storage: Storage, storageKey: string): Session | null {
  const raw = storage.getItem(storageKey)

  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Partial<Session>

    // Une session d'avant la rotation (sans jeton de rafraîchissement) ou dont le
    // rafraîchissement est lui-même expiré n'a aucune valeur : autant la nettoyer.
    if (
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.refreshExpiresAt !== 'string' ||
      new Date(parsed.refreshExpiresAt).getTime() <= Date.now()
    ) {
      storage.removeItem(storageKey)
      return null
    }

    return parsed as Session
  } catch {
    storage.removeItem(storageKey)
    return null
  }
}

function toSession(response: SignInResponse): Session {
  return {
    token: response.accessToken,
    expiresAt: response.expiresAt,
    refreshToken: response.refreshToken,
    refreshExpiresAt: response.refreshExpiresAt,
    fullName: response.fullName,
    email: response.email,
    role: response.role,
    organizationId: response.organizationId,
  }
}

interface AuthProviderProps {
  children: ReactNode
  /** Clé du stockage local ; le portail d'administration passe la sienne. */
  storageKey?: string
  /** Le portail d'administration n'a pas de modules souscrits : il ne les demande pas. */
  withModules?: boolean
}

export function AuthProvider({ children, storageKey = STORAGE_KEY, withModules = true }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(() => loadSession(storageKey))
  const [modules, setModules] = useState<ActivityModule[]>([])
  const sessionRef = useRef<Session | null>(session)
  sessionRef.current = session

  // Une session rouverte depuis le stockage de l'onglet n'avait pas demandé à être retenue :
  // la rotation du jeton la réécrit au même endroit, jamais dans le stockage local.
  const rememberRef = useRef<boolean>(window.localStorage.getItem(storageKey) !== null || window.sessionStorage.getItem(storageKey) === null)

  const store = useCallback((next: Session | null) => {
    // Les deux stockages sont purgés à chaque écriture : une session n'en occupe qu'un.
    window.localStorage.removeItem(storageKey)
    window.sessionStorage.removeItem(storageKey)
    if (next !== null) {
      storageOf(rememberRef.current).setItem(storageKey, JSON.stringify(next))
    }
    sessionRef.current = next
    setSession(next)
  }, [storageKey])

  // Une seule rotation à la fois : dix appels qui reçoivent 401 ensemble partagent la
  // même promesse, sinon le second consommerait un jeton que le premier vient de faire tourner.
  const refreshing = useRef<Promise<string | null> | null>(null)

  useEffect(() => {
    setSessionRefresher(() => {
      if (refreshing.current !== null) {
        return refreshing.current
      }

      const current = sessionRef.current
      if (current === null) {
        return Promise.resolve(null)
      }

      refreshing.current = api
        .refreshSession(current.refreshToken)
        .then((response) => {
          const next = toSession(response)
          store(next)
          return next.token
        })
        .catch(() => {
          store(null)
          return null
        })
        .finally(() => {
          refreshing.current = null
        })

      return refreshing.current
    })

    return () => setSessionRefresher(null)
  }, [store])

  // Les modules souscrits déterminent le menu : ils sont chargés dès qu'une session existe.
  useEffect(() => {
    if (!session || !withModules) {
      setModules([])
      return
    }

    const controller = new AbortController()

    api
      .listModules(session.token, controller.signal)
      .then(setModules)
      .catch(() => setModules([]))

    return () => controller.abort()
  }, [session, withModules])

  const signIn = useCallback(
    async (email: string, password: string, remember = true) => {
      const response = await api.signIn(email, password)
      rememberRef.current = remember
      store(toSession(response))
    },
    [store],
  )

  const adopt = useCallback((response: SignInResponse) => {
    rememberRef.current = true
    store(toSession(response))
  }, [store])

  const signOut = useCallback(() => {
    const current = sessionRef.current
    store(null)

    // Révocation côté serveur, sans attendre : l'écran n'a pas à dépendre du réseau
    // pour se déconnecter, mais la session ne doit pas survivre en base.
    if (current !== null) {
      void api.signOut(current.refreshToken).catch(() => undefined)
    }
  }, [store])

  const value = useMemo<AuthValue>(
    () => ({ session, modules, signIn, adopt, signOut }),
    [session, modules, signIn, adopt, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext)

  if (context === null) {
    throw new Error('useAuth doit être appelé à l’intérieur de <AuthProvider>.')
  }

  return context
}

/**
 * Purge la session et renvoie à la connexion. Une session du mauvais rôle pour ce bundle
 * (portail client ou portail d'administration) n'y a rien à faire. La purge passe par un
 * effet : appeler `signOut` pendant le rendu mettrait à jour le contexte en plein rendu.
 */
export function SessionRefusee() {
  const { signOut } = useAuth()

  useEffect(() => {
    signOut()
  }, [signOut])

  return null
}
